import { useMemo, useState } from 'react'
import { computeDiff, formatConfigValue, type DiffNode } from './config-diff'
import { colors } from '../../components/ui'

export interface VersionSide {
  readonly version: number
  readonly config: Record<string, unknown>
  readonly meta?: { changedAt?: string | null; pushedFrom?: string | null; changeReason?: string | null }
}

export interface ConfigVersionDiffViewProps {
  readonly left: VersionSide
  readonly right: VersionSide
  readonly differencesOnly?: boolean
}

// Count leaf-level differences. left-only / right-only object values have empty
// children and count as a single change (the whole added/removed subtree).
function countChanges(node: DiffNode): number {
  const keys = Object.keys(node.children)
  if (keys.length === 0) return node.status === 'equal' ? 0 : 1
  return keys.reduce((sum, k) => sum + countChanges(node.children[k]!), 0)
}

const GRID = 'minmax(160px, 1.2fr) 1fr 1fr'

const rowBg: Record<DiffNode['status'], string> = {
  equal: 'transparent',
  changed: '#fffbeb',
  'left-only': '#fef2f2',
  'right-only': '#f0fdf4',
}

const rowStyle = (status: DiffNode['status']): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: '0.75rem',
  padding: '0.25rem 0.5rem',
  alignItems: 'start',
  background: rowBg[status],
  borderRadius: '3px',
})

function ValueCell({ text }: { text: string }) {
  return (
    <span
      style={{
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        color: text === '—' ? colors.textMuted : colors.text,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      }}
    >
      {text}
    </span>
  )
}

function DiffRows({ node, differencesOnly, depth }: { node: DiffNode; differencesOnly: boolean; depth: number }) {
  return (
    <>
      {Object.entries(node.children).map(([key, child]) => {
        if (differencesOnly && !child.hasDifferences) return null
        const isObject = Object.keys(child.children).length > 0
        const keyStyle: React.CSSProperties = {
          paddingLeft: `${depth * 16}px`,
          fontWeight: isObject ? 600 : 500,
          color: isObject ? colors.textHeading : colors.text,
          fontSize: '0.8125rem',
        }
        if (isObject) {
          return (
            <div key={key}>
              <div style={rowStyle('equal')}>
                <span style={keyStyle}>{key}</span>
                <span />
                <span />
              </div>
              <DiffRows node={child} differencesOnly={differencesOnly} depth={depth + 1} />
            </div>
          )
        }
        const leftText = child.status === 'right-only' ? '—' : formatConfigValue(child.leftValue)
        const rightText = child.status === 'left-only' ? '—' : formatConfigValue(child.rightValue)
        return (
          <div key={key} style={rowStyle(child.status)}>
            <span style={keyStyle}>{key}</span>
            <ValueCell text={leftText} />
            <ValueCell text={rightText} />
          </div>
        )
      })}
    </>
  )
}

export function ConfigVersionDiffView({ left, right, differencesOnly = true }: ConfigVersionDiffViewProps) {
  const [diffOnly, setDiffOnly] = useState(differencesOnly)
  const diff = useMemo(() => computeDiff(left.config, right.config), [left.config, right.config])
  const changes = useMemo(() => countChanges(diff), [diff])

  const metaLine = (label: string, side: VersionSide) => {
    const parts: string[] = []
    if (side.meta?.changedAt) parts.push(new Date(side.meta.changedAt).toLocaleString())
    if (side.meta?.pushedFrom) parts.push(`via ${side.meta.pushedFrom}`)
    if (side.meta?.changeReason) parts.push(`— ${side.meta.changeReason}`)
    return (
      <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
        {label} v{side.version}
        {parts.length > 0 ? ` · ${parts.join(' ')}` : ''}
      </div>
    )
  }

  const columnHeaderStyle: React.CSSProperties = { fontSize: '0.75rem', color: colors.textMuted, fontWeight: 600 }

  return (
    <div
      style={{ marginTop: '0.75rem', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '0.75rem' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontWeight: 600, color: colors.textHeading, fontSize: '0.875rem' }}>
          v{left.version} → v{right.version} — {changes} {changes === 1 ? 'change' : 'changes'}
        </div>
        <button
          type="button"
          onClick={() => setDiffOnly((v) => !v)}
          style={{
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: colors.textMuted,
            fontSize: '0.75rem',
            padding: '0.2rem 0.5rem',
          }}
        >
          {diffOnly ? 'Differences only: on' : 'Differences only: off'}
        </button>
      </div>
      <div style={{ marginBottom: '0.5rem' }}>
        {metaLine('Left', left)}
        {metaLine('Right', right)}
      </div>
      {diff.hasDifferences ? (
        <>
          <div
            style={{
              ...rowStyle('equal'),
              borderBottom: `1px solid ${colors.border}`,
              borderRadius: 0,
              marginBottom: '0.25rem',
            }}
          >
            <span style={columnHeaderStyle}>Key</span>
            <span style={columnHeaderStyle}>v{left.version}</span>
            <span style={columnHeaderStyle}>v{right.version}</span>
          </div>
          <DiffRows node={diff} differencesOnly={diffOnly} depth={0} />
        </>
      ) : (
        <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>
          No differences between v{left.version} and v{right.version}.
        </div>
      )}
    </div>
  )
}
